import * as React from 'react';
import { IDisposable } from '@agilepoint/wat';
import { ILocalizer, ITranslateFunction } from './localizer';

const localizerContext = React.createContext<ILocalizer>({} as ILocalizer);

export const LocalizationProvider = localizerContext.Provider;

export interface ILocalizeProps {
    translate: string;
}

export function Localize(props: ILocalizeProps) {
    const { translate } = props;

    const renderLocalized = React.useCallback((localizer: ILocalizer) => localizer.translate(translate), [translate]);

    return (
        <localizerContext.Consumer>
            {renderLocalized}
        </localizerContext.Consumer>
    );
}

export function withTranslate<TProps extends { t: ITranslateFunction; }>(component: React.ComponentType<TProps>) {

    return function WithTranslte(props: Omit<TProps, 't'>) {
        const renderComponent = React.useCallback((localizer: ILocalizer) => React.createElement(component, { ...props, t: localizer.translate } as TProps), [props]);

        return (
            <localizerContext.Consumer>
                {renderComponent}
            </localizerContext.Consumer>
        );
    };
}

export function withLocalizer<TProps extends { localizer: ILocalizer; }>(component: React.ComponentType<TProps>) {

    return function WithTranslte(props: Omit<TProps, 'localizer'>) {
        const renderComponent = React.useCallback((localizer: ILocalizer) => React.createElement(component, { ...props, localizer } as TProps), [props]);

        return (
            <localizerContext.Consumer>
                {renderComponent}
            </localizerContext.Consumer>
        );
    };
}

export function injectLocalizationProvider<TProps extends { localizer: ILocalizer }>(component: React.ComponentType<TProps>) {
    return class LocalizationProviderInjector extends React.Component<TProps> {

        _subscriptions: IDisposable[] = [];
        doUpdate = () => {
            this.forceUpdate();
        }

        override componentDidMount() {
            const { localizer } = this.props;
            this._subscriptions.push(localizer.subscribe('initialized', this.doUpdate));
            this._subscriptions.push(localizer.subscribe('languageChanged', this.doUpdate));
        }

        override componentWillUnmount() {
            this._subscriptions.forEach(i => i.dispose());
        }

        override render() {
            return (
                <localizerContext.Provider value={this.props.localizer}>
                    {React.createElement(component, this.props)}
                </localizerContext.Provider>
            );
        }
    };
}

export function useTranslate() {
    const { translate } = React.useContext(localizerContext);
    return translate;
}

export function useDirection(){
    const { dir } = React.useContext(localizerContext);
    return dir;
}